import type { UploadFile } from '@edwin-group/people-graphql';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import classNames from 'classnames';
import { CustomImage } from '@/components/CustomImage';
import { Tag } from '@/components/Tag';
import MediaDuration from '../MediaDuration';

type KnowledgeBaseCardProps = {
  title: string;
  description: string;
  thumbnail?: UploadFile;
  url: string;
  progress?: number;
  descriptionClasses?: string;
  button?: ReactNode;
  favorite?: ReactNode;
  tag?: string;
  duration?: string;
  onClick?: () => void;
  truncateDescription?: boolean;
  onClickButton?: () => void;
  noImage?: boolean;
  watchLabel?: string;
};

const KnowledgeBaseCard = ({
  title,
  description,
  thumbnail,
  noImage = false,
  url,
  progress,
  button,
  favorite,
  descriptionClasses,
  tag,
  duration,
  onClick,
  truncateDescription = true,
  onClickButton,
  watchLabel,
}: KnowledgeBaseCardProps) => {
  const { src } = useResponsiveImage(thumbnail);

  return (
    <div className="flex flex-col gap-sh-16 rounded-sh-16 border border-teal-accent bg-light-green p-sh-16 md:flex-row md:gap-sh-25">
      {thumbnail && !noImage && (
        <div className=" w-full shrink-0 overflow-hidden rounded-t-sh-16 bg-people-green-200 md:w-[359px] md:rounded-sh-16">
          <CustomImage
            src={src}
            alt={thumbnail.alternativeText || ''}
            className={classNames('h-full w-full bg-cover bg-center', onClick ? 'cursor-pointer' : '')}
            onClick={onClick}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-sh-16 pt-0 md:pt-sh-16">
        <div className="flex items-start justify-between gap-5">
          <h2 className="flex-1 font-haffer text-sh-20-30 font-bold text-black">{title}</h2>
          {favorite}
        </div>
        {tag ? (
          <div className="flex">
            <Tag label={tag} bgColor="bg-white" />
          </div>
        ) : null}
        <div>
          <p
            className={classNames(
              'mb-[8px] font-haffersq text-sh-18-28 text-black',
              descriptionClasses || '',
              truncateDescription ? 'line-clamp-2' : '',
            )}
            title={description}>
            {description}
          </p>
          {duration && <MediaDuration duration={duration} />}
        </div>
        {progress && progress > 0 ? (
          <div className="h-[6px] w-full rounded-full bg-light-green">
            <div
              className="h-[6px] rounded-full bg-people-green"
              style={{ width: `${progress <= 100 ? progress : 100}%` }}></div>
          </div>
        ) : null}
        <div>
          {!button ? (
            <Link
              to={url}
              className="font-haffer text-sh-12-18 font-bold text-dark-green"
              onClick={() => {
                if (onClickButton) {
                  onClickButton();
                }
              }}>
              {watchLabel || 'Learn more'}
            </Link>
          ) : (
            button
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseCard;
